import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx-js-style';
import { Box, Typography } from '@mui/material';
import { ExportButton } from '../../../common_components/FormInputs';
import { StyledCurrency } from '../../../helper/StyledCurrency';
import CommonTable from '../../../common_components/CommonTable';
import { SUMMARY_ROW_POSITIONS, EXPAND_TRIGGERS } from '../../../common_components/tableConstants';
import TableToolbar from '../../../common_components/TableToolbar';
import { useSelector, useDispatch } from 'react-redux';
import CommonLoader from '../../../common_components/CommonLoader';
import { fetchPreferences, savePreferences, selectPreferences, selectPreferencesLoading, updateNetworkPreference, } from '../../../redux/divergenceSlice';
const BREAKDOWN_NETWORK_KEY = 'performance_client_report';
const getHiddenColumnsFromPreferences = (preferences, networkKey) => {
    const hiddenSet = new Set();
    if (!preferences?.[networkKey])
        return hiddenSet;
    preferences[networkKey].forEach(item => {
        const metricKey = Object.keys(item)[0];
        const config = item[metricKey];
        if (config?.visibility === false) {
            hiddenSet.add(metricKey);
        }
    });
    return hiddenSet;
};
const getColumnOrderFromPreferences = (preferences, networkKey, defaultMetrics) => {
    if (!preferences?.[networkKey] || preferences[networkKey].length === 0) {
        return defaultMetrics;
    }
    const orderedMetrics = [];
    const preferencesKeys = [];
    preferences[networkKey].forEach(item => {
        const metricKey = Object.keys(item)[0];
        const config = item[metricKey];
        preferencesKeys.push(metricKey);
        if (config?.visibility !== false) {
            orderedMetrics.push({ key: metricKey, order: config?.order ?? 999 });
        }
    });
    orderedMetrics.sort((a, b) => a.order - b.order);
    const sortedKeys = orderedMetrics.map(m => m.key);
    const remainingMetrics = defaultMetrics.filter(m => !preferencesKeys.includes(m));
    return [...sortedKeys, ...remainingMetrics];
};
const NETWORK_COLUMN_MAP = {
    adword: ['Google Revenue', 'Google Spend', 'Google MP', 'Google ROAS'],
    meta: ['Meta Revenue', 'Meta Spend', 'Meta MP', 'Meta ROAS'],
    bing: ['Bing Revenue', 'Bing Spend', 'Bing MP', 'Bing ROAS'],
    criteo: ['Criteo Revenue', 'Criteo Spend', 'Criteo MP', 'Criteo ROAS'],
};
const PAID_NETWORKS = ['adword', 'meta', 'bing', 'criteo'];
const DailyChannelBreakdownTab = ({ data, hasShopify, hasGa, formula, networks }) => {
    const dispatch = useDispatch();
    const preferences = useSelector(selectPreferences);
    const preferencesLoading = useSelector(selectPreferencesLoading);
    const breakdownData = data?.breakdown_data || [];
    const breakdownAllTotal = data?.breakdown_alltotal || {};
    const [sortBy, setSortBy] = useState('Date');
    const [sortOrder, setSortOrder] = useState('asc');
    const [columnVisibilityAnchorEl, setColumnVisibilityAnchorEl] = useState(null);
    const allMetrics = useMemo(() => {
        if (!breakdownData.length)
            return [];
        return Object.keys(breakdownData[0]).filter(k => k !== 'Date');
    }, [breakdownData]);
    const hiddenColumns = useMemo(() => {
        const hidden = getHiddenColumnsFromPreferences(preferences, BREAKDOWN_NETWORK_KEY);
        if (networks) {
            PAID_NETWORKS.forEach(net => {
                if (!networks.includes(net)) {
                    NETWORK_COLUMN_MAP[net]?.forEach(col => hidden.add(col));
                }
            });
        }
        return hidden;
    }, [preferences, networks]);
    const columnOrder = useMemo(() => getColumnOrderFromPreferences(preferences, BREAKDOWN_NETWORK_KEY, allMetrics), [preferences, allMetrics]);
    useEffect(() => {
        dispatch(fetchPreferences({ moduleKey: 'account_performance' }));
    }, [dispatch]);
    const handleColumnVisibilityChange = (newHiddenColumns) => {
        const existingPrefs = preferences?.[BREAKDOWN_NETWORK_KEY] || [];
        const existingOrderMap = new Map();
        existingPrefs.forEach((item, index) => {
            const key = Object.keys(item)[0];
            existingOrderMap.set(key, item[key]?.order ?? index + 1);
        });
        const newPreferences = [];
        allMetrics.forEach((metric, index) => {
            const isHidden = newHiddenColumns.has(metric);
            const existingOrder = existingOrderMap.get(metric) ?? index + 1;
            newPreferences.push({
                [metric]: {
                    visibility: !isHidden,
                    order: existingOrder,
                },
            });
        });
        dispatch(updateNetworkPreference({ networkKey: BREAKDOWN_NETWORK_KEY, metrics: newPreferences }));
        const updatedPreferences = {
            ...preferences,
            [BREAKDOWN_NETWORK_KEY]: newPreferences,
        };
        dispatch(savePreferences({ preferences: updatedPreferences, moduleKey: 'account_performance' }));
    };
    const handleColumnOrderChange = (newOrder) => {
        const existingPrefs = preferences?.[BREAKDOWN_NETWORK_KEY] || [];
        const existingVisibilityMap = new Map();
        existingPrefs.forEach(item => {
            const key = Object.keys(item)[0];
            existingVisibilityMap.set(key, item[key]?.visibility ?? true);
        });
        const newPreferences = [];
        const metricsInOrder = newOrder.filter(m => m !== 'Date');
        metricsInOrder.forEach((metric, index) => {
            const isVisible = existingVisibilityMap.get(metric) ?? true;
            newPreferences.push({
                [metric]: { visibility: isVisible, order: index + 1 },
            });
        });
        allMetrics.forEach((metric, index) => {
            if (!metricsInOrder.includes(metric)) {
                const isVisible = existingVisibilityMap.get(metric) ?? true;
                newPreferences.push({
                    [metric]: { visibility: isVisible, order: metricsInOrder.length + index + 1 },
                });
            }
        });
        dispatch(updateNetworkPreference({ networkKey: BREAKDOWN_NETWORK_KEY, metrics: newPreferences }));
        const updatedPreferences = {
            ...preferences,
            [BREAKDOWN_NETWORK_KEY]: newPreferences,
        };
        dispatch(savePreferences({ preferences: updatedPreferences, moduleKey: 'account_performance' }));
    };
    const processedData = useMemo(() => {
        if (!breakdownData || !breakdownData.length)
            return [];
        return breakdownData.map((row) => {
            const newRow = { ...row };
            const keys = Object.keys(newRow);
            const convRateKey = keys.find(k => k.toLowerCase() === 'conversion rate');
            const ordersKey = keys.find(k => k.toLowerCase() === 'orders');
            const visitorsKey = keys.find(k => k.toLowerCase() === 'total visitors');
            if (convRateKey && ordersKey && visitorsKey) {
                const orders = Number(newRow[ordersKey]) || 0;
                const visitors = Number(newRow[visitorsKey]) || 0;
                if (visitors > 0) {
                    newRow[convRateKey] = (orders / visitors) * 100;
                }
                else {
                    newRow[convRateKey] = 0;
                }
            }
            return newRow;
        });
    }, [breakdownData]);
    const sortedData = useMemo(() => {
        if (!sortBy)
            return processedData;
        return [...processedData].sort((a, b) => {
            const valA = a[sortBy];
            const valB = b[sortBy];
            if (valA < valB)
                return sortOrder === 'asc' ? -1 : 1;
            if (valA > valB)
                return sortOrder === 'asc' ? 1 : -1;
            return 0;
        });
    }, [processedData, sortBy, sortOrder]);
    const handleSortChange = (id, dir) => {
        setSortBy(id);
        if (dir)
            setSortOrder(dir);
        else
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    };
    const columns = useMemo(() => {
        if (!breakdownData.length)
            return [];
        const keys = Object.keys(breakdownData[0]);
        const getTooltipText = (key) => {
            const lowerKey = key.toLowerCase();
            const connectedSource = hasShopify ? "Shopify" : (hasGa ? "Google Analytics" : "connected networks");
            if (lowerKey === 'conversion rate')
                return "((Orders / Total visitors) × 100)";
            if (lowerKey === 'revenue')
                return hasShopify ? `Fetched from Shopify (${formula || 'G-D+S+T'})` : (hasGa ? "Fetched from Google Analytics" : "Fetched from connected networks");
            if (lowerKey === 'aov')
                return "Average Order Value (Revenue / Orders)";
            if (lowerKey === 'aur')
                return "Average Unit Retail (Revenue / Units)";
            if (lowerKey === 'total mp')
                return "((Total Spend / Revenue) × 100)";
            if (lowerKey === 'total roas')
                return "(Revenue / Total Spend)";
            if (lowerKey === 'cpa')
                return "(Total Spend / Orders)";
            return undefined;
        };
        return keys.map(key => {
            let align = 'left';
            if (key === 'Date')
                align = 'left';
            return {
                id: key,
                label: key.charAt(0).toUpperCase() + key.slice(1),
                sortable: true,
                align,
                minWidth: key === 'Date' ? '120px' : '100px',
                tooltip: getTooltipText(key)
            };
        });
    }, [breakdownData]);
    const formatValue = (key, value) => {
        if (value === undefined || value === null)
            return '-';
        if (key === 'Date' && typeof value === 'string') {
            const parts = value.split('-');
            if (parts.length === 3) {
                const [year, month, day] = parts;
                return `${month}/${day}/${year}`;
            }
        }
        if (typeof value === 'number') {
            const lowerKey = key.toLowerCase();
            if (lowerKey.includes('revenue') || lowerKey.includes('spend') || lowerKey.includes('aov') || lowerKey.includes('aur') || lowerKey.includes('cpa')) {
                return `$${new Intl.NumberFormat('en-US').format(Math.abs(value))}`;
            }
            else if (lowerKey.includes('rate') || lowerKey.includes('mp')) {
                return `${value.toFixed(2)}%`;
            }
            else if (lowerKey.includes('roas')) {
                return value.toFixed(2);
            }
            else {
                return value.toLocaleString('en-US'); // For visitors, orders, units
            }
        }
        return value;
    };
    const renderCell = (row, column) => {
        const valueStr = String(formatValue(column.id, row[column.id]));
        return _jsx(StyledCurrency, { value: valueStr, fontSize: "14px", symbolSize: "14px", valueWeight: 400, symbolWeight: 400, valueColor: "#101828", symbolColor: "#98A2B3" });
    };
    const renderSummaryRow = (rows, column) => {
        if (column.id === 'Date') {
            return _jsx(Typography, { sx: { fontSize: '14px !important', fontWeight: 700 }, children: "Total" });
        }
        let valueToFormat = breakdownAllTotal[column.id];
        if (column.id.toLowerCase() === 'conversion rate') {
            const totalOrders = breakdownAllTotal['orders'] || 0;
            const totalVisitors = breakdownAllTotal['Total visitors'] || 0;
            if (totalVisitors > 0) {
                valueToFormat = (totalOrders / totalVisitors) * 100;
            }
        }
        const valueStr = String(formatValue(column.id, valueToFormat));
        return _jsx(StyledCurrency, { value: valueStr, fontSize: "14px", symbolSize: "14px", valueWeight: 700, symbolWeight: 700, valueColor: "#101828", symbolColor: "#98A2B3" });
    };
    if (preferencesLoading) {
        return (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', p: 4 }, children: _jsx(CommonLoader, { className: 'table-loader' }) }));
    }
    if (!breakdownData.length) {
        return _jsx(Typography, { sx: { p: 2, color: '#667085' }, children: "No daily breakdown data available." });
    }
    const handleExport = () => {
        if (!sortedData || sortedData.length === 0)
            return;
        const headers = columns.map(col => col.label);
        const dataRows = sortedData.map(row => {
            return columns.map(col => {
                let val = row[col.id];
                return val !== undefined && val !== null ? val : '-';
            });
        });
        const summaryRow = columns.map(col => {
            if (col.id === 'Date')
                return 'Total';
            let valueToFormat = breakdownAllTotal[col.id];
            if (col.id.toLowerCase() === 'conversion rate') {
                const totalOrders = breakdownAllTotal['orders'] || 0;
                const totalVisitors = breakdownAllTotal['Total visitors'] || 0;
                if (totalVisitors > 0) {
                    valueToFormat = (totalOrders / totalVisitors) * 100;
                }
            }
            return valueToFormat !== undefined && valueToFormat !== null ? valueToFormat : '-';
        });
        const headerRow = headers.map(h => ({
            v: h,
            t: 's',
            s: { font: { bold: true, color: { rgb: '000000' } }, fill: { fgColor: { rgb: 'D9D9D9' } } },
        }));
        const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows, summaryRow]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Daily Channel Breakdown');
        XLSX.writeFile(wb, `Daily_Channel_Breakdown.xlsx`);
    };
    return (_jsxs(Box, { sx: { width: '100%', border: '1px solid #EAECF0', borderRadius: '8px', overflow: 'hidden' }, children: [_jsx(TableToolbar, { title: "", showFullScreen: true, showRows: true, enableColumnVisibility: true, onColumnVisibilityClick: (e) => setColumnVisibilityAnchorEl(e.currentTarget), customActionsLeft: _jsx(ExportButton, { onClick: handleExport }) }), _jsx(Box, { className: "user-list-table-wrapper daily-breakdown-table", children: _jsx(CommonTable, { columns: columns, rows: sortedData, renderCell: renderCell, stickyHeader: true, maxHeight: 600, enableSummaryRow: true, summaryRowPosition: SUMMARY_ROW_POSITIONS.BOTTOM, renderSummaryRow: renderSummaryRow, isLoading: false, expandTrigger: EXPAND_TRIGGERS.NONE, sortConfig: { columnId: sortBy, direction: sortOrder }, onSortChange: handleSortChange, hiddenColumns: hiddenColumns, onColumnVisibilityChange: handleColumnVisibilityChange, columnVisibilityAnchorEl: columnVisibilityAnchorEl, onColumnVisibilityClose: () => setColumnVisibilityAnchorEl(null), columnVisibilityConfig: { enable: true, columns: allMetrics, enableReorder: true }, enableColumnReorder: true, columnOrder: ['Date', ...columnOrder], onColumnOrderChange: handleColumnOrderChange, pinnedColumns: { left: ['Date'], right: [] } }) })] }));
};
export default DailyChannelBreakdownTab;
